import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle, MapPin, Pencil } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { StudentPickerMulti } from "@/components/StudentPicker";
import { ImpegniHistoryPicker } from "@/components/mansioni/ImpegniHistoryPicker";
import { ConflictPanel } from "@/components/ConflictPanel";

const BUILTIN_KINDS = {
  ministeriale: { label: "🏛️ Ministeriale", color: "#5B7FE2" },
  visita:       { label: "🚌 Visita",        color: "#7C3AED" },
  uscita:       { label: "🚪 Uscita",        color: "#E2725B" },
  permesso:     { label: "🎫 Permesso",      color: "#F59E0B" },
  altro:        { label: "📌 Altro",         color: "#556B2F" },
};
const CUSTOM_COLOR = "#4b5563";
function labelFor(k) { return BUILTIN_KINDS[k]?.label || `📌 ${k.charAt(0).toUpperCase()}${k.slice(1)}`; }
function colorFor(k) { return BUILTIN_KINDS[k]?.color || CUSTOM_COLOR; }

export default function Impegni() {
  const qc = useQueryClient();
  const [since, setSince] = useState(new Date().toLocaleDateString("en-CA"));
  const [until, setUntil] = useState(new Date(Date.now() + 30*24*3600*1000).toLocaleDateString("en-CA"));
  const [open, setOpen] = useState(false);
  const [conflictWarn, setConflictWarn] = useState(null);
  const [assignMode, setAssignMode] = useState("individual");
  const [form, setForm] = useState(getEmpty());
  const [newKind, setNewKind] = useState("");
  const [editing, setEditing] = useState(null);

  const setStudents = (ids) => { setForm((f) => ({ ...f, student_ids: ids, force: false })); setConflictWarn(null); };
  const removeConflicting = (sid) => {
    setForm((f) => ({ ...f, student_ids: f.student_ids.filter((x) => x !== sid), force: false }));
    setConflictWarn((w) => {
      const left = (w?.conflicts || []).filter((c) => c.student_id !== sid);
      return left.length ? { ...w, conflicts: left, conflict_count: left.length } : null;
    });
  };

  function openEdit(i) {
    setEditing(i);
    setAssignMode("individual");
    setConflictWarn(null);
    setForm({
      ...getEmpty(), kind: i.kind, title: i.title || "", description: i.description || "",
      date: i.date, date_to: i.date, start_time: i.start_time || "", end_time: i.end_time || "",
      location: i.location || "", student_ids: i.student_ids || [],
    });
    setOpen(true);
  }

  function getEmpty() {
    const today = new Date().toLocaleDateString("en-CA");
    return {
      kind: "visita", title: "", description: "",
      date: today, date_to: today,
      start_time: "", end_time: "", location: "",
      student_ids: [], year_levels: [], track_types: [],
      force: false,
    };
  }

  const { data: impegni = [] } = useQuery({
    queryKey: ["impegni", { since, until }],
    queryFn: async () => (await api.get("/mansioni-area/impegni", { params: { since, until } })).data,
  });
  const { data: students = [] } = useQuery({
    queryKey: ["students-list", "resid"],
    queryFn: async () => (await api.get("/students", { params: { track_type: "residenziale" } })).data,
  });
  const { data: kinds = [] } = useQuery({
    queryKey: ["impegni-kinds"],
    queryFn: async () => (await api.get("/mansioni-area/impegni/kinds")).data,
  });

  const addKindMut = useMutation({
    mutationFn: async (name) => (await api.post("/mansioni-area/impegni/kinds", { name })).data,
    onSuccess: (d) => {
      toast.success(`Tipo "${d.name}" aggiunto`);
      qc.invalidateQueries({ queryKey: ["impegni-kinds"] });
      setForm((f) => ({ ...f, kind: d.name }));
      setNewKind("");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const createMut = useMutation({
    mutationFn: async () => (await api.post("/mansioni-area/impegni", form)).data,
    onSuccess: () => {
      toast.success("Impegno creato");
      qc.invalidateQueries({ queryKey: ["impegni"] });
      setOpen(false); setForm(getEmpty()); setConflictWarn(null);
    },
    onError: (e) => {
      const raw = e.response?.data?.detail_raw;
      if (e.response?.status === 409 && raw?.conflicts) setConflictWarn(raw);
      else toast.error(e.response?.data?.detail || "Errore creazione");
    },
  });

  const updateMut = useMutation({
    mutationFn: async () => {
      const { kind, title, description, date, start_time, end_time, location, student_ids } = form;
      return (await api.patch(`/mansioni-area/impegni/${editing.id}`, {
        kind, title, description, date, location, student_ids,
        start_time: start_time || null, end_time: end_time || null,
      })).data;
    },
    onSuccess: () => {
      toast.success("Impegno aggiornato");
      qc.invalidateQueries({ queryKey: ["impegni"] });
      setOpen(false); setEditing(null); setForm(getEmpty());
    },
    onError: (e) => {
      const raw = e.response?.data?.detail_raw;
      if (e.response?.status === 409 && raw?.conflicts) setConflictWarn(raw);
      else toast.error(e.response?.data?.detail || "Errore salvataggio");
    },
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/mansioni-area/impegni/${id}`)).data,
    onSuccess: () => { toast.success("Impegno eliminato"); qc.invalidateQueries({ queryKey: ["impegni"] }); },
  });

  const grouped = useMemo(() => {
    const map = {};
    impegni.forEach((t) => { (map[t.date] ||= []).push(t); });
    return Object.entries(map).sort(([a],[b]) => a.localeCompare(b));
  }, [impegni]);

  const toggleYear = (y) => {
    const set = new Set(form.year_levels);
    set.has(y) ? set.delete(y) : set.add(y);
    setForm({...form, year_levels: [...set]});
  };
  const toggleTrack = (t) => {
    const set = new Set(form.track_types);
    set.has(t) ? set.delete(t) : set.add(t);
    setForm({...form, track_types: [...set]});
  };

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title="Impegni Esterni"
        subtitle="Visite, uscite, impegni ministeriali. Assegna studenti singoli o intere classi."
        actions={
          <Button data-testid="impegno-add-btn" onClick={() => { setEditing(null); setAssignMode("individual"); setForm(getEmpty()); setConflictWarn(null); setOpen(true); }} className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]">
            <Plus size={14} className="mr-1.5"/> Nuovo impegno
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div><Label className="text-xs">Da</Label><Input type="date" value={since} onChange={(e) => setSince(e.target.value)} className="mt-1"/></div>
        <div><Label className="text-xs">A</Label><Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="mt-1"/></div>
        <div className="flex items-end"><div className="text-xs text-stone-500">{impegni.length} impegni · {grouped.length} giorni</div></div>
      </div>

      <div className="space-y-4">
        {grouped.length === 0 && <Card className="p-10 text-center text-sm text-stone-400 border border-stone-200 rounded-xl shadow-none">Nessun impegno nel range.</Card>}
        {grouped.map(([day, items]) => (
          <Card key={day} className="border border-stone-200 rounded-xl shadow-none overflow-hidden">
            <div className="px-4 py-2 bg-stone-50 border-b border-stone-100 text-sm font-medium">{fmtDate(day)} · {items.length} impegno/i</div>
            <div className="divide-y divide-stone-100">
              {items.map((i) => (
                <div key={i.id} className="px-4 py-3 flex items-center justify-between gap-3 text-sm" data-testid={`impegno-${i.id}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: colorFor(i.kind) }}/>
                    <div className="min-w-0">
                      <div className="font-medium truncate">
                        {i.title} <span className="text-xs text-stone-500 font-normal">{labelFor(i.kind)}</span>
                        {i.day_count > 1 && <span className="text-[10px] ml-1.5 px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-600">Giorno {(i.day_index || 0) + 1}/{i.day_count}</span>}
                      </div>
                      <div className="text-[11px] text-stone-500 truncate">
                        {i.start_time || "tutto il giorno"}{i.end_time ? ` – ${i.end_time}` : ""}
                        {i.location && ` · 📍 ${i.location}`}
                        {` · ${i.student_count || 0} studenti`}
                      </div>
                    </div>
                    {i.override_conflict && <AlertTriangle size={13} className="text-amber-600 flex-shrink-0" title="Salvato con override conflitti"/>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(i)} data-testid={`impegno-edit-${i.id}`}>
                      <Pencil size={13}/>
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => { if (confirm(`Eliminare "${i.title}"?`)) delMut.mutate(i.id); }} data-testid={`impegno-del-${i.id}`}>
                      <Trash2 size={13}/>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setConflictWarn(null); setEditing(null); } }}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica impegno" : "Nuovo impegno esterno"}</DialogTitle></DialogHeader>
          {editing?.day_count > 1 && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="impegno-edit-series-note">
              Questo impegno fa parte di una serie di {editing.day_count} giorni: la modifica vale solo per questo giorno.
            </div>
          )}
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({...form, kind: v})}>
                  <SelectTrigger data-testid="impegno-kind" className="mt-1.5"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {kinds.map((k) => <SelectItem key={k} value={k}>{labelFor(k)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={newKind}
                    onChange={(e) => setNewKind(e.target.value)}
                    placeholder="Aggiungi nuovo tipo (es. ritiro, campo missionario…)"
                    className="h-8 text-xs"
                    data-testid="impegno-new-kind"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!newKind.trim() || addKindMut.isPending}
                    onClick={() => addKindMut.mutate(newKind.trim())}
                    data-testid="impegno-add-kind"
                    className="h-8"
                  >Aggiungi</Button>
                </div>
              </div>
            </div>

            {editing ? (
              <div>
                <Label>Data</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value, date_to: e.target.value})} data-testid="impegno-edit-date" className="mt-1.5"/>
              </div>
            ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Dal</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value, date_to: form.date_to && form.date_to < e.target.value ? e.target.value : form.date_to})} data-testid="impegno-date" className="mt-1.5"/>
              </div>
              <div>
                <Label>Al</Label>
                <Input type="date" value={form.date_to} min={form.date} onChange={(e) => setForm({...form, date_to: e.target.value})} data-testid="impegno-date-to" className="mt-1.5"/>
              </div>
            </div>
            )}

            <div><Label>Titolo</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="impegno-title" placeholder="Es. Visita a chiesa" className="mt-1.5"/></div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Ora inizio {form.date_to !== form.date && <span className="text-[10px] text-stone-500">(primo giorno)</span>}</Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm({...form, start_time: e.target.value})} className="mt-1.5"/>
              </div>
              <div>
                <Label>Ora fine {form.date_to !== form.date && <span className="text-[10px] text-stone-500">(ultimo giorno)</span>}</Label>
                <Input type="time" value={form.end_time} onChange={(e) => setForm({...form, end_time: e.target.value})} className="mt-1.5"/>
              </div>
              <div><Label>Luogo</Label><Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} placeholder="Città o indirizzo" className="mt-1.5"/></div>
            </div>

            {!editing && (
            <div>
              <Label>Modalità assegnazione</Label>
              <div className="flex gap-2 mt-1.5">
                <button type="button" onClick={() => { setAssignMode("individual"); setForm({...form, year_levels: [], track_types: []}); }} className={`px-3 py-1.5 rounded-full text-xs border ${assignMode === "individual" ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200"}`}>Studenti singoli</button>
                <button type="button" onClick={() => { setAssignMode("class"); setForm({...form, student_ids: []}); }} className={`px-3 py-1.5 rounded-full text-xs border ${assignMode === "class" ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200"}`}>Classe (anno + percorso)</button>
              </div>
            </div>
            )}

            {assignMode === "individual" ? (
              <div>
                <Label>Studenti ({form.student_ids.length} selezionati)</Label>
                <StudentPickerMulti students={students} values={form.student_ids} onChange={setStudents} testId="imp-students"/>
                <ImpegniHistoryPicker students={students} values={form.student_ids} onChange={setStudents}/>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Anni</Label>
                  <div className="flex gap-1.5 mt-1.5">
                    {[1,2,3].map((y) => (
                      <button key={y} type="button" onClick={() => toggleYear(y)} className={`px-3 py-1 rounded-full text-xs border ${form.year_levels.includes(y) ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200"}`}>{y}°</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Percorsi</Label>
                  <div className="flex gap-1.5 mt-1.5">
                    {["residenziale","modulare"].map((t) => (
                      <button key={t} type="button" onClick={() => toggleTrack(t)} className={`px-3 py-1 rounded-full text-xs border capitalize ${form.track_types.includes(t) ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200"}`}>{t}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div><Label>Descrizione</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={2} className="mt-1.5"/></div>

            <ConflictPanel warn={conflictWarn} force={form.force} onForceChange={(v) => setForm({...form, force: v})} testIdPrefix="impegno"
              onRemoveStudent={assignMode === "individual" ? removeConflicting : undefined}/>
            {!conflictWarn && !editing && form.student_ids.length > 0 && createMut.isError && (
              <div className="text-xs text-stone-600 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" data-testid="impegno-recheck-hint">
                Studenti modificati: premi «Crea impegno» per verificare di nuovo le sovrapposizioni.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="impegno-save" className="bg-[#1C352D] hover:bg-[#264A3E]" disabled={!form.title || createMut.isPending || updateMut.isPending || (editing && form.student_ids.length === 0) || (conflictWarn && !form.force)} onClick={() => (editing ? updateMut.mutate() : createMut.mutate())}>
              {createMut.isPending || updateMut.isPending ? "Salvataggio…" : editing ? "Salva modifiche" : (form.force && conflictWarn ? "Salva (con override)" : "Crea impegno")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
